import { parse as parseInterval, semitonesOf, transpose, type Interval } from './intervals';
import { midiOf, parseNote, pitchClassOf, sortNotes, toTonalPc, type Spelled } from './notes';

/**
 * Chord catalog, stored as intervals from the root for the same reason scales
 * are: a diminished seventh must spell as a stack of minor thirds (C E♭ G♭ B♭♭),
 * which a semitone-based representation cannot express.
 */

export type ChordQuality = 'major' | 'minor' | 'dim' | 'aug' | 'dom' | 'sus' | 'other';

export interface ChordDef {
  id: string;
  /** Suffix appended to the root, as in "maj7" giving "Cmaj7". */
  symbol: string;
  name: string;
  aliases: readonly string[];
  intervals: readonly string[];
  quality: ChordQuality;
  /** Number of notes. Derived, but stored for quick filtering. */
  size: number;
  tags: readonly ('triad' | 'seventh' | 'extended' | 'altered' | 'quartal' | 'added' | 'sixth')[];
}

const C = (
  id: string,
  symbol: string,
  name: string,
  intervals: string,
  quality: ChordQuality,
  tags: ChordDef['tags'],
  aliases: string[] = [],
): ChordDef => {
  const list = intervals.split(' ');
  return { id, symbol, name, aliases, intervals: list, quality, size: list.length, tags };
};

export const CHORDS: readonly ChordDef[] = [
  // ------------------------------------------------------------------ triads
  C('major', '', 'Major', 'P1 M3 P5', 'major', ['triad'], ['M', 'maj']),
  C('minor', 'm', 'Minor', 'P1 m3 P5', 'minor', ['triad'], ['min', '-']),
  C('dim', 'dim', 'Diminished', 'P1 m3 d5', 'dim', ['triad'], ['o', '°']),
  C('aug', 'aug', 'Augmented', 'P1 M3 A5', 'aug', ['triad'], ['+', '#5']),
  C('sus2', 'sus2', 'Suspended 2nd', 'P1 M2 P5', 'sus', ['triad']),
  C('sus4', 'sus4', 'Suspended 4th', 'P1 P4 P5', 'sus', ['triad'], ['sus']),
  C('five', '5', 'Power chord', 'P1 P5', 'other', ['triad'], ['no3']),

  // -------------------------------------------------------------- added/sixth
  C('add9', 'add9', 'Added ninth', 'P1 M3 P5 M9', 'major', ['added']),
  C('madd9', 'madd9', 'Minor added ninth', 'P1 m3 P5 M9', 'minor', ['added']),
  C('add11', 'add11', 'Added eleventh', 'P1 M3 P5 P11', 'major', ['added']),
  C('six', '6', 'Major sixth', 'P1 M3 P5 M6', 'major', ['sixth'], ['maj6']),
  C('m6', 'm6', 'Minor sixth', 'P1 m3 P5 M6', 'minor', ['sixth']),
  C('six-nine', '6/9', 'Six nine', 'P1 M3 P5 M6 M9', 'major', ['sixth', 'extended']),

  // ---------------------------------------------------------------- sevenths
  C('maj7', 'maj7', 'Major seventh', 'P1 M3 P5 M7', 'major', ['seventh'], ['M7', 'Δ7', 'Δ']),
  C('dom7', '7', 'Dominant seventh', 'P1 M3 P5 m7', 'dom', ['seventh']),
  C('m7', 'm7', 'Minor seventh', 'P1 m3 P5 m7', 'minor', ['seventh'], ['min7', '-7']),
  C('m7b5', 'm7♭5', 'Half diminished', 'P1 m3 d5 m7', 'dim', ['seventh'], ['ø7', 'ø', 'm7b5']),
  C('dim7', 'dim7', 'Diminished seventh', 'P1 m3 d5 d7', 'dim', ['seventh'], ['o7', '°7']),
  C('mMaj7', 'mMaj7', 'Minor major seventh', 'P1 m3 P5 M7', 'minor', ['seventh'], ['mM7', '-Δ7']),
  C('augMaj7', 'maj7#5', 'Augmented major seventh', 'P1 M3 A5 M7', 'aug', ['seventh'], ['+Δ7']),
  C('aug7', '7#5', 'Augmented seventh', 'P1 M3 A5 m7', 'aug', ['seventh'], ['+7']),
  C('7sus4', '7sus4', 'Dominant seventh suspended', 'P1 P4 P5 m7', 'sus', ['seventh']),

  // ------------------------------------------------------------------ ninths
  C('maj9', 'maj9', 'Major ninth', 'P1 M3 P5 M7 M9', 'major', ['extended'], ['Δ9']),
  C('dom9', '9', 'Dominant ninth', 'P1 M3 P5 m7 M9', 'dom', ['extended']),
  C('m9', 'm9', 'Minor ninth', 'P1 m3 P5 m7 M9', 'minor', ['extended'], ['-9']),
  C('7b9', '7♭9', 'Dominant seventh flat ninth', 'P1 M3 P5 m7 m9', 'dom', ['altered']),
  C('7s9', '7♯9', 'Dominant seventh sharp ninth', 'P1 M3 P5 m7 A9', 'dom', ['altered'], ['7#9']),

  // -------------------------------------------------------------- elevenths
  C('dom11', '11', 'Dominant eleventh', 'P1 P5 m7 M9 P11', 'dom', ['extended']),
  C('m11', 'm11', 'Minor eleventh', 'P1 m3 P5 m7 M9 P11', 'minor', ['extended']),
  C(
    'maj7s11',
    'maj7♯11',
    'Major seventh sharp eleventh',
    'P1 M3 P5 M7 A11',
    'major',
    ['extended'],
    ['maj7#11'],
  ),
  C(
    '7s11',
    '7♯11',
    'Dominant seventh sharp eleventh',
    'P1 M3 P5 m7 A11',
    'dom',
    ['altered'],
    ['7#11'],
  ),

  // ------------------------------------------------------------- thirteenths
  C('dom13', '13', 'Dominant thirteenth', 'P1 M3 P5 m7 M9 M13', 'dom', ['extended']),
  C('maj13', 'maj13', 'Major thirteenth', 'P1 M3 P5 M7 M9 M13', 'major', ['extended']),
  C('m13', 'm13', 'Minor thirteenth', 'P1 m3 P5 m7 M9 M13', 'minor', ['extended']),
  C('7b13', '7♭13', 'Dominant seventh flat thirteenth', 'P1 M3 P5 m7 m13', 'dom', ['altered']),

  // ---------------------------------------------------------------- altered
  C('7alt', '7alt', 'Altered dominant', 'P1 M3 m7 m9 A9 A11 m13', 'dom', ['altered']),
  C('7b9s11', '7♭9♯11', 'Dominant flat ninth sharp eleventh', 'P1 M3 P5 m7 m9 A11', 'dom', [
    'altered',
  ]),
  C('7b5', '7♭5', 'Dominant seventh flat fifth', 'P1 M3 d5 m7', 'dom', ['altered']),

  // -------------------------------------------------------- quartal/quintal
  C('quartal3', 'quartal', 'Quartal (3 note)', 'P1 P4 m7', 'other', ['quartal']),
  C('quintal3', 'quintal', 'Quintal (3 note)', 'P1 P5 M9', 'other', ['quartal']),
];

/**
 * Chord suffixes are written with typographic glyphs for display ("7♭9♯11") but
 * users and code type ASCII ("7b9#11"). Both sides of every lookup go through
 * this, so the two spellings always meet.
 */
function normalizeSuffix(text: string): string {
  return text
    .toLowerCase()
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b')
    .replace(/Δ/gi, 'maj')
    .replace(/°/g, 'dim')
    .replace(/ø/g, 'm7b5')
    .trim();
}

const BY_ID = new Map(CHORDS.map((c) => [c.id, c]));
const BY_SYMBOL = new Map<string, ChordDef>();
for (const c of CHORDS) {
  BY_SYMBOL.set(normalizeSuffix(c.symbol), c);
  BY_SYMBOL.set(normalizeSuffix(c.id), c);
  for (const a of c.aliases) BY_SYMBOL.set(normalizeSuffix(a), c);
}

export function getChord(idOrSymbol: string): ChordDef {
  const found = BY_ID.get(idOrSymbol) ?? BY_SYMBOL.get(idOrSymbol.toLowerCase());
  if (found === undefined) throw new Error(`Unknown chord type: "${idOrSymbol}"`);
  return found;
}

export function chordExists(idOrSymbol: string): boolean {
  return BY_ID.has(idOrSymbol) || BY_SYMBOL.has(idOrSymbol.toLowerCase());
}

export function chordsByTag(tag: ChordDef['tags'][number]): ChordDef[] {
  return CHORDS.filter((c) => c.tags.includes(tag));
}

/* ------------------------------------------------------------------- notes */

/** The written notes of a chord in root position. */
export function chordNotes(root: Spelled, chordId: string): Spelled[] {
  return getChord(chordId).intervals.map((text) => transpose(root, parseInterval(text)));
}

/**
 * Chord notes with an inversion applied: the lowest `n` notes move up an octave.
 * Inversion 0 is root position, 1 is first inversion, and so on.
 */
export function chordNotesInverted(root: Spelled, chordId: string, inversion: number): Spelled[] {
  const notes = chordNotes(root, chordId);
  if (inversion <= 0) return notes;
  const n = inversion % notes.length;
  const moved = notes.slice(0, n).map((x) => ({ ...x, octave: x.octave + 1 }));
  return sortNotes([...notes.slice(n), ...moved]);
}

export function chordPitchClasses(root: Spelled, chordId: string): number[] {
  return chordNotes(root, chordId).map((n) => pitchClassOf(n));
}

/** The note that sits in the bass for a given inversion. */
export function bassNote(root: Spelled, chordId: string, inversion: number): Spelled {
  const notes = chordNotes(root, chordId);
  const note = notes[inversion % notes.length];
  if (note === undefined) throw new Error(`Invalid inversion ${inversion} for ${chordId}`);
  return note;
}

/* ----------------------------------------------------------------- symbols */

export interface ChordSymbol {
  root: Spelled;
  chordId: string;
  /** Bass note when it is not the root, as in "C/E". */
  bass?: Spelled;
}

export function formatSymbol(
  symbol: ChordSymbol,
  style: 'lead-sheet' | 'jazz' = 'lead-sheet',
): string {
  const def = getChord(symbol.chordId);
  const root = toTonalPc(symbol.root).replace('#', '♯').replace(/b/g, '♭');
  let suffix = def.symbol;

  if (style === 'jazz') {
    suffix = suffix.replace('maj7', 'Δ7').replace('maj9', 'Δ9').replace('m7♭5', 'ø7');
  }

  const slash =
    symbol.bass !== undefined && pitchClassOf(symbol.bass) !== pitchClassOf(symbol.root)
      ? `/${toTonalPc(symbol.bass).replace('#', '♯').replace(/b/g, '♭')}`
      : '';

  return `${root}${suffix}${slash}`;
}

const SYMBOL_RE = /^([A-Ga-g](?:#{1,2}|b{1,2}|♯|♭)?)(.*?)(?:\/([A-Ga-g](?:#{1,2}|b{1,2}|♯|♭)?))?$/;

/** Parses "F#m7b5/A" and friends. Throws on anything it cannot place. */
export function parseSymbol(text: string): ChordSymbol {
  const normalized = text.trim().replace(/♯/g, '#').replace(/♭/g, 'b').replace(/Δ/g, 'maj');
  const match = SYMBOL_RE.exec(normalized);
  if (match === null) throw new Error(`Cannot parse chord symbol: "${text}"`);

  const [, rootText, suffixRaw, bassText] = match;
  const root = parseNote(rootText as string);
  const suffix = (suffixRaw ?? '').trim();

  const def =
    suffix === ''
      ? getChord('major')
      : (BY_SYMBOL.get(normalizeSuffix(suffix)) ?? findBySuffix(suffix));

  if (def === undefined) throw new Error(`Unknown chord suffix: "${suffix}" in "${text}"`);

  const out: ChordSymbol = { root, chordId: def.id };
  if (bassText !== undefined) out.bass = parseNote(bassText);
  return out;
}

function findBySuffix(suffix: string): ChordDef | undefined {
  const wanted = normalizeSuffix(suffix);
  // Longest symbol first, so "maj7" is not shadowed by "maj".
  return [...CHORDS]
    .sort((a, b) => b.symbol.length - a.symbol.length)
    .find(
      (c) =>
        normalizeSuffix(c.symbol) === wanted ||
        c.aliases.some((a) => normalizeSuffix(a) === wanted),
    );
}

/* -------------------------------------------------------------- identifying */

export interface ChordMatch {
  chordId: string;
  rootPc: number;
  inversion: number;
  /** 1 means every note is accounted for with nothing extra. */
  score: number;
}

/**
 * Ranks chord candidates for a set of pitch classes. The bass, when known,
 * decides the inversion, which is what distinguishes C/E from an Em♭6.
 */
export function identifyChord(pitchClasses: readonly number[], bassPc?: number): ChordMatch[] {
  const target = new Set(pitchClasses.map((p) => ((p % 12) + 12) % 12));
  const matches: ChordMatch[] = [];

  for (const def of CHORDS) {
    for (let rootPc = 0; rootPc < 12; rootPc += 1) {
      const pcs = new Set(def.intervals.map((t) => (rootPc + semitonesOf(parseInterval(t))) % 12));

      const hits = [...pcs].filter((p) => target.has(p)).length;
      const extra = [...target].filter((p) => !pcs.has(p)).length;
      const missing = pcs.size - hits;
      if (hits === 0) continue;

      const score = hits / (hits + extra + missing);
      if (score < 0.6) continue;

      const inversion =
        bassPc === undefined ? 0 : inversionForBass(def, rootPc, ((bassPc % 12) + 12) % 12);
      if (inversion < 0) continue;

      matches.push({ chordId: def.id, rootPc, inversion, score });
    }
  }

  // Exact matches first, then the simpler chord, then the lower inversion.
  return matches.sort(
    (a, b) =>
      b.score - a.score ||
      getChord(a.chordId).size - getChord(b.chordId).size ||
      a.inversion - b.inversion,
  );
}

function inversionForBass(def: ChordDef, rootPc: number, bassPc: number): number {
  for (let i = 0; i < def.intervals.length; i += 1) {
    const pc = (rootPc + semitonesOf(parseInterval(def.intervals[i] as string))) % 12;
    if (pc === bassPc) return i;
  }
  return -1; // the bass is not a chord tone
}

/* ---------------------------------------------------------- figured bass */

/** Figured-bass symbols for triad and seventh inversions. */
export function figuredBass(chordId: string, inversion: number): string {
  const size = getChord(chordId).size;
  if (size === 3) return ['', '6', '6/4'][inversion] ?? '';
  if (size === 4) return ['7', '6/5', '4/3', '4/2'][inversion] ?? '7';
  return '';
}

export function inversionName(inversion: number): string {
  return (
    ['root position', 'first inversion', 'second inversion', 'third inversion'][inversion] ??
    `inversion ${inversion}`
  );
}

/* ------------------------------------------------------------ jazz voicings */

/** Root, third and seventh only: the core of a comping voicing. */
export function shellVoicing(root: Spelled, chordId: string): Spelled[] {
  const notes = chordNotes(root, chordId);
  const wanted = [0, 1, 3].filter((i) => i < notes.length);
  return wanted.map((i) => notes[i] as Spelled);
}

/** Drops the second-highest note of a close voicing by an octave. */
export function drop2(voicing: readonly Spelled[]): Spelled[] {
  if (voicing.length < 3) return [...voicing];
  const sorted = sortNotes(voicing);
  const idx = sorted.length - 2;
  const dropped = { ...(sorted[idx] as Spelled), octave: (sorted[idx] as Spelled).octave - 1 };
  return sortNotes([...sorted.slice(0, idx), ...sorted.slice(idx + 1), dropped]);
}

/** Drops the third-highest note of a close voicing by an octave. */
export function drop3(voicing: readonly Spelled[]): Spelled[] {
  if (voicing.length < 4) return [...voicing];
  const sorted = sortNotes(voicing);
  const idx = sorted.length - 3;
  const dropped = { ...(sorted[idx] as Spelled), octave: (sorted[idx] as Spelled).octave - 1 };
  return sortNotes([...sorted.slice(0, idx), ...sorted.slice(idx + 1), dropped]);
}

/** A stack of fourths from a root, the McCoy Tyner sound. */
export function quartalVoicing(root: Spelled, count = 3): Spelled[] {
  const out = [root];
  let current = root;
  const fourth: Interval = { number: 4, quality: 'P' };
  for (let i = 1; i < count; i += 1) {
    current = transpose(current, fourth);
    out.push(current);
  }
  return out;
}

/** Sorts a voicing and reports its total span in semitones. */
export function voicingSpan(voicing: readonly Spelled[]): number {
  if (voicing.length === 0) return 0;
  const sorted = sortNotes(voicing);
  return midiOf(sorted[sorted.length - 1] as Spelled) - midiOf(sorted[0] as Spelled);
}
